/**
 * Hooks for Grida editor to load and save page config drafts
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { frappe } from "@/lib/frappe-client";
import { logger } from "@/lib/utils/logger";

interface PageConfigDraftResponse {
  ok?: boolean;
  data?: any;
  error?: string;
}

/**
 * Hook to load page config draft for Grida editor
 */
export function useGridaPageConfigDraft(pageConfigName: string) {
  return useQuery<PageConfigDraftResponse, Error>({
    queryKey: ["grida", "page-config-draft", pageConfigName],
    queryFn: async () => {
      try {
        const result = await frappe.call<PageConfigDraftResponse>(
          "zaviago_backend.storefront.grida_editor.get_grida_page_config_draft",
          { page_config_name: pageConfigName }
        );
        return result;
      } catch (error) {
        logger.error("[useGridaPageConfigDraft] Failed to load draft:", error);
        throw error;
      }
    },
    enabled: !!pageConfigName,
  });
}

/**
 * Hook to save page config draft from Grida editor
 */
export function useSaveGridaPageConfigDraft(pageConfigName: string) {
  const queryClient = useQueryClient();

  return useMutation<PageConfigDraftResponse, Error, { sections_json?: string; [key: string]: any }>({
    mutationFn: async (data) => {
      try {
        const result = await frappe.call<PageConfigDraftResponse>(
          "zaviago_backend.storefront.grida_editor.save_grida_page_config_draft",
          {
            page_config_name: pageConfigName,
            ...data,
          }
        );
        return result;
      } catch (error) {
        logger.error("[useSaveGridaPageConfigDraft] Failed to save draft:", error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate draft query to refetch
      queryClient.invalidateQueries({ queryKey: ["grida", "page-config-draft", pageConfigName] });
    },
  });
}

/**
 * Hook to get preview URL for Grida editor
 */
export function useGridaPreviewUrl(pageConfigName: string, tenant?: string) {
  return useQuery<{ ok?: boolean; preview_url?: string; error?: string }, Error>({
    queryKey: ["grida", "preview-url", pageConfigName, tenant],
    queryFn: async () => {
      try {
        const result = await frappe.call<{ ok?: boolean; preview_url?: string; error?: string }>(
          "zaviago_backend.storefront.grida_editor.get_grida_preview_url",
          {
            page_config_name: pageConfigName,
            tenant: tenant || undefined,
          }
        );
        return result;
      } catch (error) {
        logger.error("[useGridaPreviewUrl] Failed to get preview URL:", error);
        throw error;
      }
    },
    enabled: !!pageConfigName,
  });
}
